import { Ref, ref, ComputedRef, computed } from 'vue'
import { getVehicleTypeGroups } from '@/api/vehicle-type-group/getVehicleTypeGroups'
import { useLoading, Loading } from '@/composable/useLoading'
import { VehicleTypeGroup } from '@/types/vehicle-type-group'
import { updateVehicleTypeGroup } from '@/api/vehicle-type-group/updateVehicleTypeGroup'
import { deleteVehicleTypeGroup } from '@/api/vehicle-type-group/deleteVehicleTypeGroup'
import { createVehicleTypeGroup } from '@/api/vehicle-type-group/createVehicleTypeGroup'

interface UseVehicleTypeGroup {
  data: Ref<VehicleTypeGroup[]>
  fetchAll: () => Promise<VehicleTypeGroup[]>
  remove: (uuid: string) => Promise<null>
  add: (body: VehicleTypeGroup) => Promise<VehicleTypeGroup>
  update: (body: VehicleTypeGroup) => Promise<VehicleTypeGroup>
  loading: ComputedRef<Loading>
}

export function useVehicleTypeGroup(
  initFetchAll?: boolean
): UseVehicleTypeGroup {
  const data = ref<VehicleTypeGroup[]>([])
  const { state } = useLoading()

  async function fetchAll() {
    state.getAll = true
    return new Promise<VehicleTypeGroup[]>((resolve, reject) => {
      getVehicleTypeGroups()
        .then(({ data: vehicleTypeGroups }) => {
          data.value = vehicleTypeGroups
          resolve(vehicleTypeGroups)
        })
        .catch(reject)
        .finally(() => {
          state.getAll = false
        })
    })
  }

  async function remove(uuid: string) {
    state.delete = true
    return new Promise<null>((resolve, reject) => {
      deleteVehicleTypeGroup(uuid)
        .then(() => {
          data.value = data.value.filter((item) => item.uuid !== uuid)
          resolve(null)
        })
        .catch(reject)
        .finally(() => {
          state.delete = false
        })
    })
  }

  async function add(body: VehicleTypeGroup) {
    state.create = true
    return new Promise<VehicleTypeGroup>((resolve, reject) => {
      createVehicleTypeGroup(body)
        .then(({ data: vehicleTypeGroup }) => {
          data.value.push(vehicleTypeGroup)
          resolve(vehicleTypeGroup)
        })
        .catch(reject)
        .finally(() => {
          state.create = false
        })
    })
  }

  async function update(body: VehicleTypeGroup) {
    state.update = true
    return new Promise<VehicleTypeGroup>((resolve, reject) => {
      updateVehicleTypeGroup(body)
        .then(({ data: vehicleTypeGroup }) => {
          data.value = data.value.map((item) => {
            if (item.uuid === vehicleTypeGroup.uuid) {
              return {
                ...item,
                ...vehicleTypeGroup,
              }
            }

            return item
          })
          resolve(vehicleTypeGroup)
        })
        .catch(reject)
        .finally(() => {
          state.update = false
        })
    })
  }

  if (initFetchAll) {
    fetchAll()
  }

  return {
    data,
    fetchAll,
    add,
    remove,
    update,
    loading: computed(() => state),
  }
}
